import * as React from "react";
import { Box, Stack } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  useGetResourceByIdQuery,
  useCreateResourceMutation,
  useEditResourceMutation,
} from "../../services/resource.service";
import { Resource } from "../../types/resource";
import { BaseModal, StyledTextField, Button } from "../../components/global";

interface ModalResourceFormProps {
  open: boolean;
  onClose: () => void;
  data: Resource | null;
}

const ModalResourceForm = ({ open, onClose, data }: ModalResourceFormProps) => {
  const { data: resource } = useGetResourceByIdQuery(data?.id!, {
    skip: !data?.id,
  });

  const [add, { isLoading: adding }] = useCreateResourceMutation();
  const [edit, { isLoading: editing }] = useEditResourceMutation();

  const {
    handleSubmit,
    clearErrors,
    control,
    setValue,
    formState: { errors },
  } = useForm();

  const onSubmit = async (payload: any) => {
    try {
      if (!data) await add(payload).unwrap();
      else {
        payload.id = data?.id!;
        await edit(payload).unwrap();
      }
      onClose();
    } catch (err) {
      onClose();
    }
  };

  React.useEffect(() => {
    if (open) {
      setValue("title", data?.title || "");
      setValue("category", data?.category || "");
      setValue("url", data?.url || "");
      clearErrors();
    }
  }, [data, open, setValue, clearErrors]);

  React.useEffect(() => {
    if (resource) {
      setValue("title", resource?.title || "");
      setValue("category", resource?.category || "");
      setValue("url", resource?.url || "");
    }
  }, [resource, setValue]);

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title={`${!data ? "Create" : "Edit"} Resource`}
      maxWidth="xs"
      useGlassCard={false}
      footer={
        <>
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <LoadingButton
            type="submit"
            variant="contained"
            loading={adding || editing}
            onClick={handleSubmit(onSubmit)}
          >
            {!data ? "Create" : "Update"}
          </LoadingButton>
        </>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Controller
              name="title"
              control={control}
              rules={{ required: true }}
              render={({ field: { onChange, onBlur, value, ref } }) => (
                <StyledTextField
                  label="Resource Title"
                  value={value || ""}
                  fullWidth
                  onChange={onChange}
                  onBlur={onBlur}
                  inputRef={ref}
                  error={!!errors.title}
                  helperText={!!errors.title && "This field is required"}
                />
              )}
            />
            <Controller
              name="category"
              control={control}
              rules={{ required: true }}
              render={({ field: { onChange, onBlur, value, ref } }) => (
                <StyledTextField
                  label="Category"
                  value={value || ""}
                  fullWidth
                  onChange={onChange}
                  onBlur={onBlur}
                  inputRef={ref}
                  error={!!errors.category}
                  helperText={!!errors.category && "This field is required"}
                />
              )}
            />
            <Controller
              name="url"
              control={control}
              rules={{ required: true }}
              render={({ field: { onChange, onBlur, value, ref } }) => (
                <StyledTextField
                  label="Resource URL"
                  value={value || ""}
                  fullWidth
                  type="url"
                  onChange={onChange}
                  onBlur={onBlur}
                  inputRef={ref}
                  error={!!errors.url}
                  helperText={!!errors.url && "This field is required"}
                />
              )}
            />
          </Stack>
        </Box>
      </form>
    </BaseModal>
  );
};
export default ModalResourceForm;
